import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Save, Loader2, Users, FileText, Landmark, Home, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FicheTechniqueTab({ buildingId, token }) {
  const [fiche, setFiche] = useState({});
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ficheRes, buildingRes] = await Promise.all([
          fetch(`${API_URL}/api/carnet-entretien/fiche-technique/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/buildings/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (ficheRes.ok) setFiche(await ficheRes.json());
        if (buildingRes.ok) setBuilding(await buildingRes.json());
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [buildingId, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/carnet-entretien/fiche-technique/${buildingId}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(fiche)
        }
      );
      if (response.ok) toast.success('Fiche technique enregistrée');
      else toast.error('Erreur');
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Fiche technique de l'immeuble</h2>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892] disabled:opacity-50" data-testid="save-fiche-btn">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          Enregistrer
        </button>
      </div>

      {/* Section 1: Identification de la copropriété */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-[#00D3A8]" />
          1. Identification de la copropriété
        </h3>
        {building && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="font-medium text-gray-900">{building.nom || 'Sans nom'}</p>
            <p className="text-sm text-gray-600">{building.adresse}</p>
            <p className="text-sm text-gray-600">{building.code_postal} {building.ville}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Année de construction</label>
            <input type="number" value={fiche.annee_construction || ''} onChange={(e) => setFiche({...fiche, annee_construction: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="1975" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Nombre de bâtiments</label>
            <input type="number" value={fiche.nombre_batiments || ''} onChange={(e) => setFiche({...fiche, nombre_batiments: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="1" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Nombre de cages d'escalier</label>
            <input type="number" value={fiche.nombre_cages || ''} onChange={(e) => setFiche({...fiche, nombre_cages: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="2" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Nombre d'étages</label>
            <input type="number" value={fiche.nombre_etages || ''} onChange={(e) => setFiche({...fiche, nombre_etages: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Lots principaux</label>
            <input type="number" value={fiche.nombre_lots_principaux || ''} onChange={(e) => setFiche({...fiche, nombre_lots_principaux: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Lots secondaires</label>
            <input type="number" value={fiche.nombre_lots_secondaires || ''} onChange={(e) => setFiche({...fiche, nombre_lots_secondaires: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Caves, parkings..." />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Surface totale (m²)</label>
            <input type="number" value={fiche.surface_totale || ''} onChange={(e) => setFiche({...fiche, surface_totale: parseFloat(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Usage</label>
            <select value={fiche.usage || ''} onChange={(e) => setFiche({...fiche, usage: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="habitation">Habitation</option>
              <option value="mixte">Mixte (habitation + commercial)</option>
              <option value="tertiaire">Tertiaire (bureaux)</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Informations administratives */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-[#00D3A8]" />
          2. Informations administratives
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Type de syndic</label>
            <select value={fiche.type_syndic || ''} onChange={(e) => setFiche({...fiche, type_syndic: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="professionnel">Professionnel</option>
              <option value="benevole">Bénévole (non professionnel)</option>
              <option value="cooperatif">Coopératif</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Nom du syndic</label>
            <input type="text" value={fiche.syndic_nom || ''} onChange={(e) => setFiche({...fiche, syndic_nom: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Cabinet XYZ" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Adresse du syndic</label>
            <input type="text" value={fiche.syndic_adresse || ''} onChange={(e) => setFiche({...fiche, syndic_adresse: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Téléphone syndic</label>
            <input type="tel" value={fiche.syndic_telephone || ''} onChange={(e) => setFiche({...fiche, syndic_telephone: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Email syndic</label>
            <input type="email" value={fiche.syndic_email || ''} onChange={(e) => setFiche({...fiche, syndic_email: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date début mandat</label>
            <input type="date" value={fiche.syndic_date_mandat || ''} onChange={(e) => setFiche({...fiche, syndic_date_mandat: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm text-gray-600 mb-1">Références règlement de copropriété</label>
            <input type="text" value={fiche.reglement_references || ''} onChange={(e) => setFiche({...fiche, reglement_references: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Date publication, notaire, numéro d'enregistrement..." />
          </div>
        </div>
      </div>

      {/* Section 3: Description technique */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <Landmark className="h-5 w-5 text-[#00D3A8]" />
          3. Description technique de l'immeuble
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Structure / Gros œuvre</label>
            <select value={fiche.structure || ''} onChange={(e) => setFiche({...fiche, structure: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="beton">Béton armé</option>
              <option value="pierre">Pierre de taille</option>
              <option value="brique">Brique</option>
              <option value="mixte">Mixte</option>
              <option value="ossature_bois">Ossature bois</option>
              <option value="metal">Structure métallique</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Type de toiture</label>
            <select value={fiche.toiture_type || ''} onChange={(e) => setFiche({...fiche, toiture_type: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="terrasse">Terrasse (toit plat)</option>
              <option value="tuiles">Tuiles</option>
              <option value="ardoises">Ardoises</option>
              <option value="zinc">Zinc</option>
              <option value="bac_acier">Bac acier</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date dernière réfection toiture</label>
            <input type="date" value={fiche.toiture_derniere_refection || ''} onChange={(e) => setFiche({...fiche, toiture_derniere_refection: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Matériau façades</label>
            <select value={fiche.facades_materiau || ''} onChange={(e) => setFiche({...fiche, facades_materiau: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="enduit">Enduit</option>
              <option value="pierre">Pierre apparente</option>
              <option value="brique">Brique</option>
              <option value="bardage">Bardage (bois/métal)</option>
              <option value="mixte">Mixte</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date dernier ravalement</label>
            <input type="date" value={fiche.facades_dernier_ravalement || ''} onChange={(e) => setFiche({...fiche, facades_dernier_ravalement: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">État général façades</label>
            <select value={fiche.facades_etat || ''} onChange={(e) => setFiche({...fiche, facades_etat: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="bon">Bon état</option>
              <option value="moyen">État moyen</option>
              <option value="travaux_prevoir">Travaux à prévoir</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm text-gray-600 mb-1">Description des parties communes intérieures</label>
          <textarea value={fiche.parties_communes || ''} onChange={(e) => setFiche({...fiche, parties_communes: e.target.value})} rows="3" className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="Hall d'entrée, escaliers, couloirs, locaux techniques, caves, parking..." />
        </div>
      </div>

      {/* Section 4: Chauffage & Eau */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <span className="text-xl">🔥</span>
          4. Chauffage & Eau
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Type de chauffage</label>
            <select value={fiche.type_chauffage || ''} onChange={(e) => setFiche({...fiche, type_chauffage: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="collectif_gaz">Collectif gaz</option>
              <option value="collectif_fioul">Collectif fioul</option>
              <option value="collectif_urbain">Réseau urbain</option>
              <option value="individuel_gaz">Individuel gaz</option>
              <option value="individuel_electrique">Individuel électrique</option>
              <option value="pompe_chaleur">Pompe à chaleur collective</option>
              <option value="geothermie">Géothermie</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Eau chaude sanitaire</label>
            <select value={fiche.type_eau_chaude || ''} onChange={(e) => setFiche({...fiche, type_eau_chaude: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="collective">Collective</option>
              <option value="individuelle_gaz">Individuelle gaz</option>
              <option value="individuelle_electrique">Individuelle électrique</option>
              <option value="solaire">Solaire + appoint</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Année installation chaufferie</label>
            <input type="number" value={fiche.chaufferie_annee || ''} onChange={(e) => setFiche({...fiche, chaufferie_annee: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" placeholder="2015" />
          </div>
        </div>
      </div>

      {/* Section 5: Équipements collectifs */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <Layers className="h-5 w-5 text-[#00D3A8]" />
          5. Équipements collectifs présents
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { key: 'ascenseur', label: 'Ascenseur(s)' },
            { key: 'monte_charge', label: 'Monte-charge' },
            { key: 'parking_souterrain', label: 'Parking souterrain' },
            { key: 'parking_exterieur', label: 'Parking extérieur' },
            { key: 'local_velos', label: 'Local vélos' },
            { key: 'local_poubelles', label: 'Local poubelles' },
            { key: 'espaces_verts', label: 'Espaces verts' },
            { key: 'aire_jeux', label: 'Aire de jeux' },
            { key: 'gardien', label: 'Gardien / Concierge' },
            { key: 'loge_gardien', label: 'Loge gardien' },
            { key: 'digicode', label: 'Digicode' },
            { key: 'interphone', label: 'Interphone' },
            { key: 'videophone', label: 'Visiophone' },
            { key: 'controle_acces', label: 'Contrôle d\'accès badges' },
            { key: 'videosurveillance', label: 'Vidéosurveillance' },
            { key: 'portail_automatique', label: 'Portail automatique' },
            { key: 'porte_garage', label: 'Porte de garage automatique' },
            { key: 'vmc', label: 'VMC collective' },
            { key: 'antenne_collective', label: 'Antenne TV collective' },
            { key: 'fibre', label: 'Fibre optique' },
            { key: 'sprinkler', label: 'Sprinkler / Extinction auto' },
            { key: 'desenfumage', label: 'Désenfumage' },
            { key: 'alarme_incendie', label: 'Alarme incendie' },
            { key: 'piscine', label: 'Piscine' },
          ].map(item => (
            <label key={item.key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
              <input type="checkbox" checked={fiche[item.key] || false} onChange={(e) => setFiche({...fiche, [item.key]: e.target.checked})} className="w-4 h-4 text-[#00D3A8] rounded" />
              <span className="text-sm text-gray-700">{item.label}</span>
            </label>
          ))}
        </div>
        {fiche.ascenseur && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <label className="block text-sm text-gray-600 mb-1">Nombre d'ascenseurs</label>
            <input type="number" value={fiche.nombre_ascenseurs || ''} onChange={(e) => setFiche({...fiche, nombre_ascenseurs: parseInt(e.target.value) || 0})} className="w-24 px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
        )}
      </div>

      {/* Section complémentaire : Diagnostics */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5 text-[#00D3A8]" />
          6. Diagnostics et documents réglementaires
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">DTA Amiante (Dossier Technique)</label>
            <select value={fiche.dta_amiante || ''} onChange={(e) => setFiche({...fiche, dta_amiante: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="non_concerne">Non concerné (après 1997)</option>
              <option value="a_jour">À jour</option>
              <option value="a_mettre_a_jour">À mettre à jour</option>
              <option value="non_realise">Non réalisé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date DTA Amiante</label>
            <input type="date" value={fiche.dta_amiante_date || ''} onChange={(e) => setFiche({...fiche, dta_amiante_date: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">CREP Plomb</label>
            <select value={fiche.crep_plomb || ''} onChange={(e) => setFiche({...fiche, crep_plomb: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="non_concerne">Non concerné (après 1949)</option>
              <option value="a_jour">À jour</option>
              <option value="presence_plomb">Présence de plomb</option>
              <option value="non_realise">Non réalisé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">DPE Collectif</label>
            <select value={fiche.dpe_collectif || ''} onChange={(e) => setFiche({...fiche, dpe_collectif: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="a">Classe A</option>
              <option value="b">Classe B</option>
              <option value="c">Classe C</option>
              <option value="d">Classe D</option>
              <option value="e">Classe E</option>
              <option value="f">Classe F</option>
              <option value="g">Classe G</option>
              <option value="non_realise">Non réalisé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date DPE</label>
            <input type="date" value={fiche.dpe_date || ''} onChange={(e) => setFiche({...fiche, dpe_date: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Audit énergétique</label>
            <select value={fiche.audit_energetique || ''} onChange={(e) => setFiche({...fiche, audit_energetique: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg">
              <option value="">Sélectionner</option>
              <option value="realise">Réalisé</option>
              <option value="a_realiser">À réaliser</option>
              <option value="non_concerne">Non concerné</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
